import { useEffect, useState } from "react";
import { getConnectorStatus, type ConnectorStatus } from "../api/connectors";

/**
 * Connector health for the nav badge.
 *
 * The Connectors page polls every 15s because you are sitting there watching
 * it; the sidebar is on screen all day, so it settles for a minute. A failed
 * fetch keeps the last good answer rather than clearing the badge — a blip in
 * the API is not evidence that the PLCs came back.
 */

const REFRESH_MS = 60_000;

export type ConnectorHealth = {
  offline: ConnectorStatus[];
  degraded: ConnectorStatus[];
};

const EMPTY: ConnectorHealth = { offline: [], degraded: [] };

/** Pass `enabled: false` to skip polling entirely (non-admins never see the badge). */
export const useConnectorHealth = (enabled: boolean): ConnectorHealth => {
  const [health, setHealth] = useState<ConnectorHealth>(EMPTY);

  useEffect(() => {
    if (!enabled) {
      setHealth(EMPTY);
      return;
    }

    const ctrl = new AbortController();
    let alive = true;

    const load = async () => {
      try {
        const rows = await getConnectorStatus(ctrl.signal);
        if (!alive) return;
        setHealth({
          offline: rows.filter((c) => c.status === "offline"),
          degraded: rows.filter((c) => c.status === "degraded"),
        });
      } catch {
        /* keep the last good health */
      }
    };

    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
      ctrl.abort();
    };
  }, [enabled]);

  return health;
};

export default useConnectorHealth;
